import { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { CalendarDays, Utensils, SlidersHorizontal, CreditCard, Minus, Plus } from "lucide-react";
import DashboardLayoutComponent from "../../../components/common/Dashboard/Dashboard";
import { Button, Input } from "../../../components";
import Popup from "../../../components/common/Popup/Popup";
import { MealSlotIcon } from "../../../components/common/MealSlotIcon/MealSlotIcon";
import { AuditLogTable } from "../../../components/common/AuditLogTable/AuditLogTable";
import { useManageSubscriptions } from "./useManageSubscriptions";
import { FieldLabel } from "./FieldLabel";
import { TastePreferenceFields } from "../../../components/common/TastePreferenceFields/TastePreferenceFields";
import { formatTastePreference, sameFlavours } from "../../../tastePreferences";
import { purchaseOverlapsActiveSubs } from "../../Plans/useSubscription";
import { isValidEmail, isValidMobile, sanitizeMobileInput } from "../../../utils";
import {
  PLANS,
  LUNCH_DINNER_OPTIONS,
  MEAL_TYPES,
  CARB_TYPES,
  STATUSES,
  MEAL_COUNT_FIELDS,
  PAYMENT_METHODS,
} from "./constants";

const capitalize = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : "");

const formatDate = (dateValue) => {
  if (!dateValue) return "—";
  return new Date(dateValue).toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const toInputDate = (dateValue) => {
  if (!dateValue) return "";
  return new Date(dateValue).toISOString().split("T")[0];
};

// end date only exists once a plan stops delivering — updatedAt is when that happened
const getSubscriptionEndLabel = (sub) => {
  if (sub.status === "completed" || sub.status === "cancelled") {
    return sub.updatedAt ? formatDate(sub.updatedAt) : "—";
  }
  if (sub.status === "active") return "Ongoing";
  return "Not started";
};

const STATUS_COLORS = {
  active: "bg-green-100 text-green-700",
  queued: "bg-amber-100 text-amber-700",
  completed: "bg-red-100 text-red-700",
  cancelled: "bg-red-100 text-red-700",
};

const emptyCreateForm = {
  plan: "",
  totalMeals: "",
  lunchDinner: "",
  mealType: "",
  carbType: "",
  subscriptionStartDate: "",
  allergy: "",
  spiceLevel: "",
  flavourPreferences: [],
  paymentMethod: "",
  paymentId: "",
  reason: "",
};

const renderFieldLabel = (text, help) => <FieldLabel label={text} help={help} />;

const emptyNewUserForm = {
  firstName: "",
  lastName: "",
  email: "",
  mobile: "",
  postalAddress: "",
  password: "",
};

const generatePassword = () =>
  Math.random().toString(36).slice(-8) + Math.floor(Math.random() * 10);

export const ManageSubscriptions = () => {
  const {
    allUsers,
    isLoadingUsers,
    selectedUser,
    isLoadingUserDetail,
    selectUser,
    clearSelectedUser,
    auditLogs,
    isLoadingLogs,
    fetchAuditLogs,
    sendMobileOtp,
    verifyMobileOtp,
    createUser,
    createSubscription,
    updateSubscription,
    closeAccount,
  } = useManageSubscriptions();

  // Arriving here from another page's "Edit User"/"Edit Subscription" link
  // (e.g. All Registered Users) with a userId in router state — select that
  // user and jump straight into editing their subscription, skipping the
  // manual search-then-click-Edit steps.
  const location = useLocation();
  const pendingAutoEditUserId = useRef(location.state?.userId || null);

  useEffect(() => {
    if (pendingAutoEditUserId.current) {
      selectUser(pendingAutoEditUserId.current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState("subscriptions"); // 'subscriptions' | 'auditLog'

  // Arriving here from "All Registered Users" → "+ Add New User" with
  // openNewUser in router state — skip straight to the New User modal instead
  // of landing on the plain search screen.
  const [isNewUserOpen, setIsNewUserOpen] = useState(() => Boolean(location.state?.openNewUser));
  const [newUserForm, setNewUserForm] = useState(emptyNewUserForm);
  const [newUserError, setNewUserError] = useState(null);
  const [isSavingNewUser, setIsSavingNewUser] = useState(false);
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [createStep, setCreateStep] = useState(0);
  const [createForm, setCreateForm] = useState(emptyCreateForm);
  const [createError, setCreateError] = useState(null);
  const [isSavingCreate, setIsSavingCreate] = useState(false);

  const [editingSub, setEditingSub] = useState(null);
  const [editFieldUpdates, setEditFieldUpdates] = useState({});
  const [editMealDeltas, setEditMealDeltas] = useState({});
  const [editReason, setEditReason] = useState("");
  const [editError, setEditError] = useState(null);
  const [editSection, setEditSection] = useState("plan");
  const [isSavingEdit, setIsSavingEdit] = useState(false);

  const [isCloseAccountOpen, setIsCloseAccountOpen] = useState(false);
  const [closeAccountReason, setCloseAccountReason] = useState("");
  const [closeAccountError, setCloseAccountError] = useState(null);
  const [isClosingAccount, setIsClosingAccount] = useState(false);

  // Whether the plan being created would queue behind an existing active plan
  // for the selected user — if so, whatever Subscription Start Date is picked
  // gets overwritten with the real activation date once the current plan
  // actually finishes, so asking the admin to guess one only invites the
  // exact confusion this is meant to avoid.
  const activeSubsForCreate = (selectedUser?.subscriptions || []).filter((s) => s.status === "active");
  const createWouldOverlap =
    !!createForm.lunchDinner && purchaseOverlapsActiveSubs(createForm.lunchDinner, activeSubsForCreate);

  const filteredUsers =
    searchQuery.trim().length > 0
      ? allUsers.filter(
          (u) =>
            `${u.firstName} ${u.lastName}`.toLowerCase().includes(searchQuery.toLowerCase()) ||
            u.email.toLowerCase().includes(searchQuery.toLowerCase())
        )
      : [];

  const handleSelectUser = async (userId) => {
    setSearchQuery("");
    setActiveTab("subscriptions");
    await selectUser(userId);
  };

  const handleShowAuditLog = () => {
    setActiveTab("auditLog");
    fetchAuditLogs(selectedUser?._id);
  };

  const openNewUserModal = () => {
    setNewUserForm({ ...emptyNewUserForm, password: generatePassword() });
    setNewUserError(null);
    setOtp("");
    setOtpSent(false);
    setOtpVerified(false);
    setIsNewUserOpen(true);
  };

  const handleNewUserChange = (field, value) => {
    const nextValue = field === "mobile" ? sanitizeMobileInput(value) : value;
    setNewUserForm((prev) => ({ ...prev, [field]: nextValue }));
    // A changed mobile number needs to be verified again
    if (field === "mobile") {
      setOtp("");
      setOtpSent(false);
      setOtpVerified(false);
    }
  };

  const handleSendOtp = async () => {
    if (!isValidMobile(newUserForm.mobile)) {
      setNewUserError("Enter a valid 10-digit mobile number first.");
      return;
    }
    try {
      setIsSendingOtp(true);
      setNewUserError(null);
      await sendMobileOtp(newUserForm.mobile, newUserForm.firstName);
      setOtpSent(true);
    } catch (err) {
      console.error("Error sending OTP:", err);
      setNewUserError(err.response?.data?.message || "Failed to send OTP.");
    } finally {
      setIsSendingOtp(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!otp) {
      setNewUserError("Enter the OTP first.");
      return;
    }
    try {
      setIsVerifyingOtp(true);
      setNewUserError(null);
      await verifyMobileOtp(newUserForm.mobile, otp);
      setOtpVerified(true);
    } catch (err) {
      console.error("Error verifying OTP:", err);
      setNewUserError(err.response?.data?.message || "Invalid OTP.");
    } finally {
      setIsVerifyingOtp(false);
    }
  };

  const handleNewUserSave = async () => {
    const { firstName, lastName, email, mobile, postalAddress, password } = newUserForm;
    if (!firstName || !lastName || !email || !mobile || !postalAddress || !password) {
      setNewUserError("All fields are required.");
      return;
    }
    if (!isValidEmail(email)) {
      setNewUserError("Please enter a valid email address.");
      return;
    }
    if (!isValidMobile(mobile)) {
      setNewUserError("Please enter a valid 10-digit mobile number.");
      return;
    }
    if (!otpVerified) {
      setNewUserError("Please verify the mobile number with OTP first.");
      return;
    }
    try {
      setIsSavingNewUser(true);
      setNewUserError(null);
      await createUser({ firstName, lastName, email, mobile, postalAddress, password, confirmPassword: password });
      setIsNewUserOpen(false);
      setSearchQuery("");
      setActiveTab("subscriptions");
    } catch (err) {
      console.error("Error creating user:", err);
      setNewUserError(err.response?.data?.message || "Failed to create user.");
    } finally {
      setIsSavingNewUser(false);
    }
  };

  const openCreateModal = () => {
    setCreateForm({ ...emptyCreateForm, subscriptionStartDate: new Date().toISOString().split("T")[0] });
    setCreateError(null);
    setCreateStep(0);
    setIsCreateOpen(true);
  };

  const handleCreateChange = (field, value) =>
    setCreateForm((prev) => ({ ...prev, [field]: value }));

  const getCreateStepError = (step) => {
    const { plan, subscriptionStartDate, lunchDinner, mealType, carbType, totalMeals, reason } = createForm;
    if (step === 0 && (!plan || !subscriptionStartDate)) return "Pick a plan and a start date to continue.";
    if (step === 1 && (!lunchDinner || !mealType || !carbType)) return "Choose meals, diet and carb type to continue.";
    if (step === 2 && (!totalMeals || Number(totalMeals) <= 0)) return "Enter a positive number of total meals to continue.";
    if (step === 3 && !reason.trim()) return "A reason is required to create this subscription.";
    return null;
  };

  const handleCreateNext = () => {
    const error = getCreateStepError(createStep);
    if (error) {
      setCreateError(error);
      return;
    }
    setCreateError(null);
    setCreateStep((step) => step + 1);
  };

  const handleCreateBack = () => {
    setCreateError(null);
    setCreateStep((step) => step - 1);
  };

  const lunchDinnerLabel = LUNCH_DINNER_OPTIONS.find((o) => o.value === createForm.lunchDinner)?.label || "—";
  const dietLabel = MEAL_TYPES.find((o) => o.value === createForm.mealType)?.label || "—";
  const carbLabel = CARB_TYPES.find((o) => o.value === createForm.carbType)?.label || "—";
  const createSummaryRows = [
    ["Customer", selectedUser ? `${selectedUser.firstName} ${selectedUser.lastName}` : "—"],
    ["Plan", createForm.plan || "—"],
    ["Total meals", createForm.totalMeals || "—"],
    [<span className="inline-flex items-center gap-1"><MealSlotIcon slot="both" />Lunch / Dinner</span>, lunchDinnerLabel],
    ["Diet", dietLabel],
    ["Carb type", carbLabel],
    ["Start", createWouldOverlap ? "Automatic (will queue)" : formatDate(createForm.subscriptionStartDate)],
    ["Allergy", createForm.allergy || "None"],
    ["Taste", formatTastePreference(createForm) || "Not specified"],
    ["Payment", createForm.paymentMethod || "No charge"],
  ];
  const CREATE_STEP_LABELS = ["Plan", "Meals & Diet", "Pricing & Duration", "Review"];

  const handleCreateSave = async () => {
    const { plan, totalMeals, lunchDinner, mealType, carbType, subscriptionStartDate, reason } = createForm;
    if (!plan || !totalMeals || !lunchDinner || !mealType || !carbType || !subscriptionStartDate || !reason.trim()) {
      setCreateError("Plan, total meals, lunch/dinner, meal type, carb type, start date and reason are all required.");
      return;
    }
    const totalMealsNum = Number(totalMeals);
    if (!Number.isFinite(totalMealsNum) || totalMealsNum <= 0) {
      setCreateError("Total meals must be a positive number.");
      return;
    }

    try {
      setIsSavingCreate(true);
      setCreateError(null);
      await createSubscription({
        userId: selectedUser._id,
        plan,
        totalMeals: totalMealsNum,
        lunchDinner,
        mealType,
        carbType,
        subscriptionStartDate,
        allergy: createForm.allergy,
        spiceLevel: createForm.spiceLevel,
        flavourPreferences: createForm.flavourPreferences,
        paymentMethod: createForm.paymentMethod,
        paymentId: createForm.paymentId,
        reason: createForm.reason,
      });
      setIsCreateOpen(false);
    } catch (err) {
      console.error("Error creating subscription:", err);
      setCreateError(err.response?.data?.message || "Failed to create subscription.");
    } finally {
      setIsSavingCreate(false);
    }
  };

  const openEditModal = (sub) => {
    setEditingSub(sub);
    setEditFieldUpdates({
      plan: sub.plan,
      carbType: sub.carbType,
      mealType: sub.mealType,
      allergy: sub.allergy || "",
      spiceLevel: sub.spiceLevel || "",
      flavourPreferences: sub.flavourPreferences || [],
      status: sub.status,
      paymentMethod: sub.paymentMethod || "",
      paymentId: sub.paymentId || "",
      subscriptionStartDate: toInputDate(sub.subscriptionStartDate),
    });
    setEditMealDeltas({ lunchMeals: 0, dinnerMeals: 0, nextDayLunchMeals: 0, nextDayDinnerMeals: 0 });
    setEditReason("");
    setEditError(null);
    setEditSection("plan");
  };

  const adjustMealDelta = (key, change) =>
    setEditMealDeltas((prev) => ({ ...prev, [key]: String((Number(prev[key]) || 0) + change) }));

  const closeEditModal = () => {
    setEditingSub(null);
    setEditError(null);
  };

  useEffect(() => {
    if (!pendingAutoEditUserId.current) return;
    if (!selectedUser || selectedUser._id !== pendingAutoEditUserId.current) return;
    const subs = selectedUser.subscriptions || [];
    const defaultSub = subs.find((s) => s.status === "active") || subs[subs.length - 1];
    if (defaultSub) openEditModal(defaultSub);
    pendingAutoEditUserId.current = null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUser]);

  const handleEditSave = async () => {
    if (!editReason.trim()) {
      setEditError("A reason is required to save this change.");
      return;
    }

    // Only send fields that actually changed
    const fieldUpdates = {};
    const stringFallbackFields = ["allergy", "spiceLevel", "paymentMethod", "paymentId"];
    Object.keys(editFieldUpdates).forEach((key) => {
      if (key === "flavourPreferences") {
        if (!sameFlavours(editFieldUpdates.flavourPreferences, editingSub.flavourPreferences)) {
          fieldUpdates.flavourPreferences = editFieldUpdates.flavourPreferences;
        }
        return;
      }
      const original = key === "subscriptionStartDate"
        ? toInputDate(editingSub.subscriptionStartDate)
        : stringFallbackFields.includes(key) ? editingSub[key] || "" : editingSub[key];
      if (editFieldUpdates[key] !== original) {
        fieldUpdates[key] = editFieldUpdates[key];
      }
    });

    const mealDeltas = {};
    Object.keys(editMealDeltas).forEach((key) => {
      const delta = Number(editMealDeltas[key]);
      if (delta) mealDeltas[key] = delta;
    });

    if (Object.keys(fieldUpdates).length === 0 && Object.keys(mealDeltas).length === 0) {
      setEditError("No changes to save — edit a field or a meal count first.");
      return;
    }

    try {
      setIsSavingEdit(true);
      setEditError(null);
      await updateSubscription(editingSub._id, { reason: editReason, fieldUpdates, mealDeltas });
      closeEditModal();
    } catch (err) {
      console.error("Error updating subscription:", err);
      setEditError(err.response?.data?.message || "Failed to update subscription.");
    } finally {
      setIsSavingEdit(false);
    }
  };

  const editSummaryRows = editingSub
    ? [
        ["Plan", editingSub.plan],
        ["Status", capitalize(editingSub.status)],
        ["Start", formatDate(editingSub.subscriptionStartDate)],
        ["End", getSubscriptionEndLabel(editingSub)],
        [<span className="inline-flex items-center gap-1"><MealSlotIcon slot="lunch" />Lunch left</span>, (editingSub.lunchMeals || 0) + (editingSub.nextDayLunchMeals || 0)],
        [<span className="inline-flex items-center gap-1"><MealSlotIcon slot="dinner" />Dinner left</span>, (editingSub.dinnerMeals || 0) + (editingSub.nextDayDinnerMeals || 0)],
        ["Diet", capitalize(editingSub.mealType)],
        ["Carb type", editingSub.carbType],
        ["Payment", editingSub.paymentMethod || (editingSub.paymentId ? "Online" : "—")],
      ]
    : [];

  const EDIT_SECTIONS = [
    { key: "plan", label: "Plan Details", hint: "Plan, status & start date", icon: CalendarDays },
    { key: "meals", label: "Meal Counts", hint: "Adjust meals left", icon: Utensils },
    { key: "preferences", label: "Preferences", hint: "Diet, carbs & allergy", icon: SlidersHorizontal },
    { key: "payment", label: "Payment Details", hint: "Method & reference", icon: CreditCard },
  ];

  const openCloseAccountModal = () => {
    setCloseAccountReason("");
    setCloseAccountError(null);
    setIsCloseAccountOpen(true);
  };

  const handleCloseAccount = async () => {
    if (!closeAccountReason.trim()) {
      setCloseAccountError("A reason is required to close this account.");
      return;
    }
    try {
      setIsClosingAccount(true);
      setCloseAccountError(null);
      const result = await closeAccount(selectedUser._id, closeAccountReason.trim());
      toast.success(
        `Account closed: ${result.subscriptionsClosed} plan(s) cancelled, ${result.cancellationRequestsDeleted} cancellation request(s) removed.`
      );
      setIsCloseAccountOpen(false);
    } catch (err) {
      console.error("Error closing account:", err);
      setCloseAccountError(err.response?.data?.message || "Failed to close account.");
    } finally {
      setIsClosingAccount(false);
    }
  };

  return (
    <DashboardLayoutComponent>
      <div className="flex flex-col justify-start items-start p-4 w-full sm:p-6 md:p-8">
        <div className="mx-auto w-full max-w-7xl">
          <div className="overflow-hidden bg-white rounded-lg shadow-md">
            <div className="p-4 md:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-3">
                <h2 className="text-xl font-bold md:text-2xl">Manage Subscriptions</h2>
                {selectedUser && (
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setActiveTab("subscriptions")}
                      className={`px-4 py-2 text-sm font-semibold rounded-md border-2 ${
                        activeTab === "subscriptions"
                          ? "bg-theme-color-1 text-white border-theme-color-1"
                          : "bg-white text-theme-color-1 border-theme-color-1"
                      }`}
                    >
                      Subscriptions
                    </button>
                    <button
                      type="button"
                      onClick={handleShowAuditLog}
                      className={`px-4 py-2 text-sm font-semibold rounded-md border-2 ${
                        activeTab === "auditLog"
                          ? "bg-theme-color-1 text-white border-theme-color-1"
                          : "bg-white text-theme-color-1 border-theme-color-1"
                      }`}
                    >
                      Audit Log
                    </button>
                  </div>
                )}
              </div>

              {/* User search */}
              <div className="mb-4">
                <div className="flex flex-col sm:flex-row gap-3">
                  <div className="flex-1">
                    <Input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search a user by name or email to manage their subscriptions..."
                    />
                  </div>
                  <button
                    type="button"
                    onClick={openNewUserModal}
                    className="px-4 py-2 text-sm font-semibold bg-white rounded-md border-2 shadow-sm text-theme-color-1 border-theme-color-1 hover:bg-theme-color-1 hover:text-white whitespace-nowrap"
                  >
                    + New User
                  </button>
                </div>
                {isLoadingUsers && <p className="text-sm text-gray-500 mt-2">Loading users...</p>}
                {searchQuery.trim().length > 0 && (
                  <div className="mt-2 max-h-64 overflow-y-auto border rounded-lg divide-y">
                    {filteredUsers.length > 0 ? (
                      filteredUsers.slice(0, 20).map((u) => (
                        <button
                          key={u._id}
                          type="button"
                          onClick={() => handleSelectUser(u._id)}
                          className="block w-full text-left px-4 py-2 hover:bg-gray-50"
                        >
                          <span className="font-semibold">{u.firstName} {u.lastName}</span>{" "}
                          <span className="text-sm text-gray-500">{u.email}</span>
                        </button>
                      ))
                    ) : (
                      <p className="px-4 py-2 text-sm text-gray-500">No matching users.</p>
                    )}
                  </div>
                )}
              </div>

              {isLoadingUserDetail && <p className="text-gray-500 py-4">Loading user...</p>}

              {selectedUser && !isLoadingUserDetail && (
                <>
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between bg-gray-50 rounded-lg p-4 mb-6 gap-3">
                    <div>
                      <p className="font-bold text-lg">{selectedUser.firstName} {selectedUser.lastName}</p>
                      <p className="text-sm text-gray-500">{selectedUser.email} · {selectedUser.mobile}</p>
                    </div>
                    <div className="flex gap-2">
                      <Button onClick={openCreateModal}>+ New Subscription</Button>
                      <button
                        type="button"
                        onClick={clearSelectedUser}
                        className="px-4 py-2 text-sm font-semibold bg-white rounded-md border-2 border-gray-300 text-gray-700 hover:bg-gray-100"
                      >
                        Change User
                      </button>
                      <button
                        type="button"
                        onClick={openCloseAccountModal}
                        className="px-4 py-2 text-sm font-semibold bg-white rounded-md border-2 border-red-600 text-red-600 hover:bg-red-600 hover:text-white"
                      >
                        Close Account
                      </button>
                    </div>
                  </div>

                  {activeTab === "subscriptions" ? (
                    <div className="overflow-x-auto">
                      {selectedUser.subscriptions && selectedUser.subscriptions.length > 0 ? (
                        <table className="w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-4 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">Plan</th>
                              <th className="px-4 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">Status</th>
                              <th className="px-4 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">Start</th>
                              <th className="px-4 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">End</th>
                              <th className="px-4 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">Meals Left</th>
                              <th className="px-4 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">Diet</th>
                              <th className="px-4 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">Carb</th>
                              <th className="px-4 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">Payment</th>
                              <th className="px-4 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">Actions</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {[...selectedUser.subscriptions].reverse().map((sub) => {
                              const lunchCount = (sub.lunchMeals || 0) + (sub.nextDayLunchMeals || 0);
                              const dinnerCount = (sub.dinnerMeals || 0) + (sub.nextDayDinnerMeals || 0);
                              return (
                                <tr key={sub._id} className="hover:bg-gray-50">
                                  <td className="px-4 py-4 text-sm font-medium text-gray-900 whitespace-nowrap">{sub.plan}</td>
                                  <td className="px-4 py-4 text-sm whitespace-nowrap">
                                    <span className={`px-2 py-1 rounded-full text-xs font-semibold ${STATUS_COLORS[sub.status] || "bg-gray-100 text-gray-700"}`}>
                                      {capitalize(sub.status)}
                                    </span>
                                  </td>
                                  <td className="px-4 py-4 text-sm text-gray-700 whitespace-nowrap">{formatDate(sub.subscriptionStartDate)}</td>
                                  <td className="px-4 py-4 text-sm text-gray-700 whitespace-nowrap">{getSubscriptionEndLabel(sub)}</td>
                                  <td className="px-4 py-4 text-sm text-gray-700 whitespace-nowrap">
                                    <span className="inline-flex items-center gap-2">
                                      <span className="inline-flex items-center gap-1"><MealSlotIcon slot="lunch" />L: {lunchCount}</span>
                                      <span className="inline-flex items-center gap-1"><MealSlotIcon slot="dinner" />D: {dinnerCount}</span>
                                    </span>
                                  </td>
                                  <td className="px-4 py-4 text-sm text-gray-700 whitespace-nowrap">{capitalize(sub.mealType)}</td>
                                  <td className="px-4 py-4 text-sm text-gray-700 whitespace-nowrap">{sub.carbType}</td>
                                  <td className="px-4 py-4 text-sm text-gray-700 whitespace-nowrap">
                                    {sub.paymentMethod || (sub.paymentId ? "Online" : "—")}
                                  </td>
                                  <td className="px-4 py-4 text-sm whitespace-nowrap">
                                    <button
                                      type="button"
                                      onClick={() => openEditModal(sub)}
                                      className="text-theme-color-1 hover:underline"
                                    >
                                      Edit
                                    </button>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      ) : (
                        <p className="py-4 text-center text-gray-500">No subscriptions for this user yet.</p>
                      )}
                    </div>
                  ) : (
                    <AuditLogTable logs={auditLogs} isLoading={isLoadingLogs} scopeLabel={`${selectedUser.firstName} ${selectedUser.lastName}`} />
                  )}
                </>
              )}

              {!selectedUser && !isLoadingUserDetail && (
                <p className="py-8 text-center text-gray-500">
                  Search and select a user above to view or manage their subscriptions.
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* New User Modal */}
      <Popup
        isOpen={isNewUserOpen}
        onClose={() => setIsNewUserOpen(false)}
        title="New User"
        content={
          <div className="space-y-4">
            {newUserError && <p className="text-sm text-red-600">{newUserError}</p>}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
              <Input
                type="text"
                value={newUserForm.firstName}
                onChange={(e) => handleNewUserChange("firstName", e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
              <Input
                type="text"
                value={newUserForm.lastName}
                onChange={(e) => handleNewUserChange("lastName", e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <Input
                type="email"
                value={newUserForm.email}
                onChange={(e) => handleNewUserChange("email", e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Mobile</label>
              <div className="flex gap-2">
                <Input
                  type="tel"
                  value={newUserForm.mobile}
                  onChange={(e) => handleNewUserChange("mobile", e.target.value)}
                  maxLength={10}
                />
                {otpVerified ? (
                  <span className="flex items-center px-3 text-xs font-semibold text-green-700 bg-green-100 rounded-md whitespace-nowrap">
                    ✓ Verified
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={handleSendOtp}
                    className="px-3 py-1 text-xs font-semibold bg-gray-100 rounded-md text-gray-700 hover:bg-gray-200 whitespace-nowrap"
                  >
                    {isSendingOtp ? "Sending..." : otpSent ? "Resend OTP" : "Send OTP"}
                  </button>
                )}
              </div>
              {otpSent && !otpVerified && (
                <div className="flex gap-2 mt-2">
                  <Input
                    type="text"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                    placeholder="Enter OTP"
                  />
                  <button
                    type="button"
                    onClick={handleVerifyOtp}
                    className="px-3 py-1 text-xs font-semibold text-white rounded-md bg-theme-color-1 hover:bg-black whitespace-nowrap"
                  >
                    {isVerifyingOtp ? "Verifying..." : "Verify OTP"}
                  </button>
                </div>
              )}
              <p className="text-xs text-gray-500 mt-1">
                Mobile number must be verified with OTP before the account can be created.
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Postal Address</label>
              <Input
                type="text"
                value={newUserForm.postalAddress}
                onChange={(e) => handleNewUserChange("postalAddress", e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
              <div className="flex gap-2">
                <Input
                  type="text"
                  value={newUserForm.password}
                  onChange={(e) => handleNewUserChange("password", e.target.value)}
                />
                <button
                  type="button"
                  onClick={() => handleNewUserChange("password", generatePassword())}
                  className="px-3 py-1 text-xs font-semibold bg-gray-100 rounded-md text-gray-700 hover:bg-gray-200 whitespace-nowrap"
                >
                  Regenerate
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Auto-generated — share it with the customer, or they can use "Forgot Password" later.
              </p>
            </div>
          </div>
        }
        buttons={[
          { label: "Cancel", onClick: () => setIsNewUserOpen(false), className: "bg-gray-100 text-gray-700 hover:bg-gray-200" },
          { label: isSavingNewUser ? "Creating..." : "Create User", onClick: handleNewUserSave, className: "bg-theme-color-1 text-white hover:bg-black" },
        ]}
      />

      {/* Create Subscription Modal */}
      <Popup
        isOpen={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        title="New Subscription"
        maxWidthClass="max-w-4xl"
        content={
          <div className="space-y-5">
            <p className="text-sm text-gray-500">
              Creating for <span className="font-semibold text-gray-900">{selectedUser ? `${selectedUser.firstName} ${selectedUser.lastName}` : "—"}</span>
            </p>

            <div className="flex gap-2 items-center">
              {CREATE_STEP_LABELS.map((label, index) => (
                <div key={label} className="flex flex-1 gap-2 items-center min-w-0">
                  <div className={`flex flex-shrink-0 justify-center items-center w-7 h-7 rounded-full text-xs font-bold ${index <= createStep ? "bg-theme-color-1 text-white" : "bg-gray-200 text-gray-500"}`}>
                    {index + 1}
                  </div>
                  <span className={`text-xs font-semibold truncate ${index === createStep ? "text-gray-900" : "text-gray-500"}`}>{label}</span>
                </div>
              ))}
            </div>

            {createError && <p className="text-sm text-red-600">{createError}</p>}

            <div className="grid gap-6 md:grid-cols-3">
              <div className="space-y-4 md:col-span-2">
                {createStep === 0 && (
                  <>
                    <div>
                      <FieldLabel label="Plan" help="The plan the customer is buying: Trial Meal Pack, Weekly Plan or Monthly Plan." />
                      <Input
                        type="select"
                        value={createForm.plan}
                        onChange={(e) => handleCreateChange("plan", e.target.value)}
                        placeholder="Select plan"
                        options={PLANS.map((p) => ({ value: p, label: p }))}
                      />
                    </div>
                    {createWouldOverlap ? (
                      <p className="text-xs text-gray-600 bg-gray-50 border border-gray-300 rounded px-2 py-1.5">
                        📅 Start date: automatic — this plan overlaps an active plan for this user, so it will queue and
                        activate on its own once the current one finishes. Nothing to pick here.
                      </p>
                    ) : (
                      <div>
                        <FieldLabel label="Subscription Start Date" help="The first day the plan starts. If you pick today and the lunch (10:30 AM) or dinner (4:00 PM) cutoff has already passed, that meal moves to the next day. This field is hidden when the plan will queue, because its start date is set automatically when it begins." />
                        <Input
                          type="date"
                          value={createForm.subscriptionStartDate}
                          onChange={(e) => handleCreateChange("subscriptionStartDate", e.target.value)}
                        />
                      </div>
                    )}
                  </>
                )}

                {createStep === 1 && (
                  <>
                    <div>
                      <FieldLabel label="Lunch / Dinner" help="Which meals the plan covers. Only Lunch or Only Dinner gives one meal a day. Both gives lunch and dinner. If the customer already has an active plan for the same meal, this plan waits in the queue and starts on its own when that one runs out." />
                      <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                        {LUNCH_DINNER_OPTIONS.map((opt) => (
                          <button
                            key={opt.value}
                            type="button"
                            onClick={() => handleCreateChange("lunchDinner", opt.value)}
                            className={`p-3 text-left rounded-xl border-2 transition-colors ${
                              createForm.lunchDinner === opt.value ? "border-theme-color-1 bg-green-50" : "border-gray-200 hover:border-gray-300"
                            }`}
                          >
                            <p className="flex items-center gap-1.5 text-sm font-semibold text-gray-900">
                              <MealSlotIcon slot={opt.value === "lunch" ? "lunch" : opt.value === "dinner" ? "dinner" : "both"} />
                              {opt.label}
                            </p>
                            <p className="text-xs text-gray-500">{opt.value === "lunchAndDinner" ? "2 meals a day" : "1 meal a day"}</p>
                          </button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <FieldLabel label="Meal Type (Diet)" help="The customer's food preference: Veg, Non-Veg, or Both (Flexible). This is about the diet only, not the meal time." />
                      <div className="flex flex-wrap gap-2">
                        {MEAL_TYPES.map((opt) => (
                          <button
                            key={opt.value}
                            type="button"
                            onClick={() => handleCreateChange("mealType", opt.value)}
                            className={`px-4 py-2 text-sm font-semibold rounded-full border ${
                              createForm.mealType === opt.value
                                ? "bg-theme-color-1 text-white border-theme-color-1"
                                : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                            }`}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <FieldLabel label="Carb Type" help="The meal style the customer chose for this plan." />
                      <Input
                        type="select"
                        value={createForm.carbType}
                        onChange={(e) => handleCreateChange("carbType", e.target.value)}
                        placeholder="Select"
                        options={CARB_TYPES}
                      />
                    </div>
                    <div>
                      <FieldLabel label="Allergy (optional)" help="Any allergy the customer has told us about. Leave blank if there is none. It shows in the Meal Delivery List." />
                      <Input
                        type="text"
                        value={createForm.allergy}
                        onChange={(e) => handleCreateChange("allergy", e.target.value)}
                        placeholder="None"
                      />
                    </div>
                    <TastePreferenceFields
                      spiceLevel={createForm.spiceLevel}
                      flavourPreferences={createForm.flavourPreferences}
                      renderLabel={renderFieldLabel}
                      onChange={(next) => setCreateForm((prev) => ({ ...prev, ...next }))}
                    />
                  </>
                )}

                {createStep === 2 && (
                  <>
                    <div>
                      <FieldLabel label="Total Meals" help="How many meals are in this plan in total. If the plan covers both lunch and dinner, the meals are split equally between the two." />
                      <Input
                        type="number"
                        value={createForm.totalMeals}
                        onChange={(e) => handleCreateChange("totalMeals", e.target.value)}
                        placeholder="e.g. 6"
                      />
                    </div>
                    <div>
                      <FieldLabel label="Payment Method (optional)" help="How the customer paid. Leave blank if there was no payment, for example a free or complimentary plan." />
                      <Input
                        type="select"
                        value={createForm.paymentMethod}
                        onChange={(e) => handleCreateChange("paymentMethod", e.target.value)}
                        placeholder="No charge / leave blank"
                        options={PAYMENT_METHODS.map((m) => ({ value: m, label: m }))}
                      />
                    </div>
                    <div>
                      <FieldLabel label="Payment Reference (optional)" help="The transaction ID, cheque number or other payment reference, kept for your records." />
                      <Input
                        type="text"
                        value={createForm.paymentId}
                        onChange={(e) => handleCreateChange("paymentId", e.target.value)}
                        placeholder="e.g. UPI transaction ID, cheque number, etc."
                      />
                    </div>
                  </>
                )}

                {createStep === 3 && (
                  <>
                    <div>
                      <FieldLabel label="Reason (required)" help="Why this subscription is being added by hand. It is saved in the Audit Log with your name." />
                      <textarea
                        value={createForm.reason}
                        onChange={(e) => handleCreateChange("reason", e.target.value)}
                        rows={3}
                        placeholder="Why is this subscription being created manually?"
                        className="block w-full rounded-lg border-0 px-5 py-2.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-theme-color-1 sm:text-sm sm:leading-6"
                      />
                    </div>
                    <p className="text-xs text-gray-500">
                      Active-vs-queued is decided automatically, same rule as checkout: this only queues if it shares a
                      meal-type track (lunch/dinner) with a plan already active for this user.
                    </p>
                  </>
                )}
              </div>

              <aside className="p-4 space-y-3 h-fit bg-green-50 rounded-xl border border-green-100">
                <p className="text-sm font-bold text-gray-900">Subscription Summary</p>
                {createSummaryRows.map(([label, value], index) => (
                  <div key={index} className="flex gap-2 justify-between text-sm">
                    <span className="text-gray-500">{label}</span>
                    <span className="font-medium text-right text-gray-900 break-words">{value}</span>
                  </div>
                ))}
              </aside>
            </div>
          </div>
        }
        buttons={
          createStep === 3
            ? [
                { label: "Back", onClick: handleCreateBack, className: "bg-gray-100 text-gray-700 hover:bg-gray-200" },
                { label: isSavingCreate ? "Saving..." : "Create Subscription", onClick: handleCreateSave, className: "bg-theme-color-1 text-white hover:bg-black" },
              ]
            : [
                createStep === 0
                  ? { label: "Cancel", onClick: () => setIsCreateOpen(false), className: "bg-gray-100 text-gray-700 hover:bg-gray-200" }
                  : { label: "Back", onClick: handleCreateBack, className: "bg-gray-100 text-gray-700 hover:bg-gray-200" },
                { label: "Next", onClick: handleCreateNext, className: "bg-theme-color-1 text-white hover:bg-black" },
              ]
        }
      />

      {/* Edit Subscription Modal */}
      <Popup
        isOpen={!!editingSub}
        onClose={closeEditModal}
        title="Edit Subscription"
        maxWidthClass="max-w-5xl"
        content={
          editingSub && (
            <div className="space-y-5">
              {editError && <p className="text-sm text-red-600">{editError}</p>}

              <div className="flex flex-wrap gap-4 justify-between items-center p-4 bg-gray-50 rounded-xl border border-gray-100">
                <div>
                  <p className="text-lg font-bold text-gray-900">
                    {selectedUser ? `${selectedUser.firstName} ${selectedUser.lastName}` : ""}
                  </p>
                  <p className="text-sm text-gray-500">{selectedUser?.email} · {selectedUser?.mobile}</p>
                </div>
                <div className="text-sm">
                  <p className="text-gray-500">Member since</p>
                  <p className="font-semibold text-gray-900">{formatDate(selectedUser?.createdAt)}</p>
                </div>
                <div className="text-sm">
                  <p className="text-gray-500">Plan</p>
                  <p className="font-semibold text-gray-900">{editingSub.plan}</p>
                </div>
                <span className={`px-3 py-1 text-sm font-semibold rounded-full ${STATUS_COLORS[editingSub.status] || "bg-gray-100 text-gray-700"}`}>
                  {capitalize(editingSub.status)}
                </span>
              </div>

              <div className="grid gap-5 md:grid-cols-4">
                <nav className="space-y-2">
                  {EDIT_SECTIONS.map((item) => (
                    <button
                      key={item.key}
                      type="button"
                      onClick={() => setEditSection(item.key)}
                      className={`w-full flex gap-3 items-center p-3 text-left rounded-xl border ${
                        editSection === item.key ? "bg-green-50 border-theme-color-1" : "bg-white border-gray-200 hover:bg-gray-50"
                      }`}
                    >
                      <item.icon className="w-5 h-5 flex-shrink-0 text-theme-color-1" />
                      <span>
                        <span className="block text-sm font-semibold text-gray-900">{item.label}</span>
                        <span className="block text-xs text-gray-500">{item.hint}</span>
                      </span>
                    </button>
                  ))}
                </nav>

                <div className="space-y-4 md:col-span-2">
                  {editSection === "plan" && (
                    <>
                      <div>
                        <FieldLabel label="Plan" help="Changes the plan name shown on this subscription. It does not change the meal counts." />
                        <Input
                          type="select"
                          value={editFieldUpdates.plan || ""}
                          onChange={(e) => setEditFieldUpdates((prev) => ({ ...prev, plan: e.target.value }))}
                          options={PLANS.map((p) => ({ value: p, label: p }))}
                        />
                      </div>
                      <div>
                        <FieldLabel label="Status" help="Active: meals are delivered from the remaining counts. Queued: waits for the current plan to finish. Completed or Cancelled: ends the plan. If it was active, its remaining meals are set to zero and any queued plan for this customer starts at the next scheduled check (10:45 AM or 4:45 PM, Monday to Saturday). Changing it back does not restore those meals. Use Adjust Meal Counts for that." />
                        <Input
                          type="select"
                          value={editFieldUpdates.status || ""}
                          onChange={(e) => setEditFieldUpdates((prev) => ({ ...prev, status: e.target.value }))}
                          options={STATUSES.map((s) => ({ value: s, label: capitalize(s) }))}
                        />
                        {editFieldUpdates.status !== editingSub.status &&
                          editingSub.status === "active" &&
                          ["cancelled", "completed"].includes(editFieldUpdates.status) && (
                            <p className="text-xs text-amber-600 mt-1">
                              This will zero out remaining meals; any queued plan for this user activates on the next
                              scheduled run.
                            </p>
                          )}
                      </div>
                      <div>
                        <FieldLabel label="Start Date" help="The day the plan starts. The change applies straight away. Est. End Date is worked out from the remaining meal counts, so it does not move just because this date changed. This is locked on a queued plan because its start date is set automatically." />
                        {editingSub.status === "queued" ? (
                          <p className="text-xs text-gray-600 bg-gray-50 border border-gray-300 rounded px-2 py-1.5">
                            📅 Start date: automatic — this plan is queued, so it'll be set to whatever date it actually
                            activates on. Editing it here wouldn't stick.
                          </p>
                        ) : (
                          <Input
                            type="date"
                            value={editFieldUpdates.subscriptionStartDate || ""}
                            onChange={(e) => setEditFieldUpdates((prev) => ({ ...prev, subscriptionStartDate: e.target.value }))}
                          />
                        )}
                        {editFieldUpdates.subscriptionStartDate !== toInputDate(editingSub.subscriptionStartDate) &&
                          editingSub.status === "active" && (
                            <p className="text-xs text-amber-600 mt-1">
                              "Meal Start Date" will update immediately. "Est. End Date" is based on today's remaining
                              meal counts, not this date, so it won't shift just from this change alone.
                            </p>
                          )}
                      </div>
                    </>
                  )}

                  {editSection === "meals" && (
                    <>
                      <div className="flex gap-2 items-start p-3 text-sm text-green-800 bg-green-50 rounded-lg border border-green-100">
                        <span>
                          Use + or − to adjust meals. Changes apply straight away. Adding lunch after 10:30 AM or
                          dinner after 4:00 PM puts those meals into the Next Day count instead of today's.
                        </span>
                      </div>
                      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        {MEAL_COUNT_FIELDS.map(({ key, label }) => {
                          const change = Number(editMealDeltas[key]) || 0;
                          return (
                            <div key={key} className="p-4 bg-white rounded-xl border border-gray-200">
                              <p className="text-sm font-semibold text-gray-900">{label}</p>
                              <p className="text-xs text-gray-500">Currently {editingSub[key] || 0}</p>
                              <div className="flex gap-3 justify-between items-center mt-3">
                                <button
                                  type="button"
                                  onClick={() => adjustMealDelta(key, -1)}
                                  className="flex justify-center items-center w-9 h-9 rounded-full border border-gray-300 hover:bg-gray-100"
                                >
                                  <Minus className="w-4 h-4" />
                                </button>
                                <span className={`text-lg font-bold ${change > 0 ? "text-green-700" : change < 0 ? "text-red-600" : "text-gray-900"}`}>
                                  {change > 0 ? `+${change}` : change}
                                </span>
                                <button
                                  type="button"
                                  onClick={() => adjustMealDelta(key, 1)}
                                  className="flex justify-center items-center w-9 h-9 rounded-full border border-gray-300 hover:bg-gray-100"
                                >
                                  <Plus className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      <p className="text-sm text-gray-600">
                        Total change: {Object.values(editMealDeltas).reduce((sum, value) => sum + (Number(value) || 0), 0)} meal(s)
                      </p>
                    </>
                  )}

                  {editSection === "preferences" && (
                    <>
                      <div>
                        <FieldLabel label="Meal Type (Diet)" help="The customer's food preference: Veg, Non-Veg, or Both (Flexible). This is about the diet only, not the meal time." />
                        <div className="flex flex-wrap gap-2">
                          {MEAL_TYPES.map((opt) => (
                            <button
                              key={opt.value}
                              type="button"
                              onClick={() => setEditFieldUpdates((prev) => ({ ...prev, mealType: opt.value }))}
                              className={`px-4 py-2 text-sm font-semibold rounded-full border ${
                                editFieldUpdates.mealType === opt.value
                                  ? "bg-theme-color-1 text-white border-theme-color-1"
                                  : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                              }`}
                            >
                              {opt.label}
                            </button>
                          ))}
                        </div>
                      </div>
                      <div>
                        <FieldLabel label="Carb Type" help="The meal style the customer chose for this plan." />
                        <Input
                          type="select"
                          value={editFieldUpdates.carbType || ""}
                          onChange={(e) => setEditFieldUpdates((prev) => ({ ...prev, carbType: e.target.value }))}
                          options={CARB_TYPES}
                        />
                      </div>
                      <div>
                        <FieldLabel label="Allergy" help="Any allergy the customer has told us about. Leave blank if there is none. It shows in the Meal Delivery List." />
                        <Input
                          type="text"
                          value={editFieldUpdates.allergy || ""}
                          onChange={(e) => setEditFieldUpdates((prev) => ({ ...prev, allergy: e.target.value }))}
                          placeholder="None"
                        />
                      </div>
                      <TastePreferenceFields
                        spiceLevel={editFieldUpdates.spiceLevel || ""}
                        flavourPreferences={editFieldUpdates.flavourPreferences || []}
                        renderLabel={renderFieldLabel}
                        onChange={(next) => setEditFieldUpdates((prev) => ({ ...prev, ...next }))}
                      />
                    </>
                  )}

                  {editSection === "payment" && (
                    <>
                      <div>
                        <FieldLabel label="Payment Method" help="How the customer paid. Leave blank if there was no payment, for example a free or complimentary plan." />
                        <Input
                          type="select"
                          value={editFieldUpdates.paymentMethod || ""}
                          onChange={(e) => setEditFieldUpdates((prev) => ({ ...prev, paymentMethod: e.target.value }))}
                          placeholder="No charge / none recorded"
                          options={PAYMENT_METHODS.map((m) => ({ value: m, label: m }))}
                        />
                      </div>
                      <div>
                        <FieldLabel label="Payment Reference" help="The transaction ID, cheque number or other payment reference, kept for your records." />
                        <Input
                          type="text"
                          value={editFieldUpdates.paymentId || ""}
                          onChange={(e) => setEditFieldUpdates((prev) => ({ ...prev, paymentId: e.target.value }))}
                          placeholder="e.g. UPI transaction ID, cheque number, etc."
                        />
                      </div>
                    </>
                  )}
                </div>

                <aside className="p-4 space-y-3 h-fit bg-green-50 rounded-xl border border-green-100">
                  <p className="text-sm font-bold text-gray-900">Current Plan Summary</p>
                  {editSummaryRows.map(([label, value], index) => (
                    <div key={index} className="flex gap-2 justify-between text-sm">
                      <span className="text-gray-500">{label}</span>
                      <span className="font-medium text-right text-gray-900 break-words">{value}</span>
                    </div>
                  ))}
                </aside>
              </div>

              <div>
                <FieldLabel label="Reason (required)" help="Why this change is being made. It is saved in the Audit Log with your name, along with the values before and after the change." />
                <textarea
                  value={editReason}
                  onChange={(e) => setEditReason(e.target.value)}
                  rows={2}
                  placeholder="Why is this change being made?"
                  className="block w-full rounded-lg border-0 px-5 py-2.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-theme-color-1 sm:text-sm sm:leading-6"
                />
              </div>
            </div>
          )
        }
        buttons={[
          { label: "Cancel", onClick: closeEditModal, className: "bg-gray-100 text-gray-700 hover:bg-gray-200" },
          { label: isSavingEdit ? "Saving..." : "Save Changes", onClick: handleEditSave, className: "bg-theme-color-1 text-white hover:bg-black" },
        ]}
      />

      <Popup
        isOpen={isCloseAccountOpen}
        onClose={() => setIsCloseAccountOpen(false)}
        title="Close Account"
        content={
          selectedUser && (
            <div className="space-y-4">
              {closeAccountError && <p className="text-sm text-red-600">{closeAccountError}</p>}
              <p className="text-sm text-gray-700">
                This will cancel every active and queued plan for {selectedUser.firstName} {selectedUser.lastName},
                set their remaining meals to zero, and delete all their cancellation requests.
              </p>
              <p className="text-xs text-gray-600 bg-gray-50 border border-gray-300 rounded px-2 py-1.5">
                It does not issue a refund, so refund separately if needed. Their customer record and payment
                history are kept, and they can still log in and buy a new plan.
              </p>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Reason (required)</label>
                <textarea
                  value={closeAccountReason}
                  onChange={(e) => setCloseAccountReason(e.target.value)}
                  rows={2}
                  placeholder="Why is this account being closed?"
                  className="block w-full rounded-lg border-0 px-5 py-2.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-theme-color-1 sm:text-sm sm:leading-6"
                />
              </div>
            </div>
          )
        }
        buttons={[
          { label: "Cancel", onClick: () => setIsCloseAccountOpen(false), className: "bg-gray-100 text-gray-700 hover:bg-gray-200" },
          { label: isClosingAccount ? "Closing..." : "Close Account", onClick: handleCloseAccount, className: "bg-red-600 text-white hover:bg-red-700" },
        ]}
      />
    </DashboardLayoutComponent>
  );
};

export default ManageSubscriptions;
